import { BaseComponent } from '../../base-component';
import { SHIPPING } from '../../_common/server';

type Recommendation = {
    brandName: string;
    image: {
        _links: {
            external: {
                href: string;
            };
        };
    };
    meta: {
        product_VariationId: string;
    };
    tracking: {
        variationId: string;
    };
    serviceInformation?: {
        deliveryOptionTypes?: string[];
    };
    _embedded: {
        ['o:variation']: {
            name: string;
            _links: {
                ['o:image']: {
                    href: string;
                };
                ['o:detail-view']: {
                    href: string;
                };
            };
        };
    };
};

export type RecommendationResponse = {
    recommendations: Recommendation[];
};

export type AlternativeWithShippingOption = {
    deliveryOptionTypes: string[];
    image: string;
    link: string;
    originalIndexFromUnfilteredResponse: number;
};

export class BetterShippingAlternativeComponent extends BaseComponent {
    public rootSelector = '.js_pdp_better-shipping-alternative__outer-frame';
    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }
        const variationId = this.getVariationId();

        const betterShippingAlternative = await this.getHaulageShippingAlternativeForVariation(variationId);

        const linkElement = this.getRootElement().querySelector('.js_pdp_better-shipping-alternative__border-frame');
        const imgElement = this.getRootElement().querySelector('.js_pdp_better-shipping-alternative__image');

        if (!betterShippingAlternative || !linkElement || !imgElement) {
            return;
        }

        imgElement.setAttribute('src', `${betterShippingAlternative.image}?h=256&w=256`);
        linkElement.setAttribute('href', betterShippingAlternative.link);

        this.show();
        this.registerClickHandler(betterShippingAlternative.originalIndexFromUnfilteredResponse);
        this.performViewTracking(betterShippingAlternative.originalIndexFromUnfilteredResponse);

        return Promise.resolve(undefined);
    }

    private extractAlternatives(response: RecommendationResponse): AlternativeWithShippingOption[] {
        const alternatives: AlternativeWithShippingOption[] = [];
        for (const r of response.recommendations) {
            if (
                !r?.serviceInformation?.deliveryOptionTypes ||
                !r?.image?._links?.external?.href ||
                !r?._embedded?.['o:variation']?._links?.['o:detail-view']?.href
            ) {
                continue;
            }

            alternatives.push({
                deliveryOptionTypes: r.serviceInformation.deliveryOptionTypes,
                image: r.image._links.external.href,
                link: r._embedded?.['o:variation']._links?.['o:detail-view'].href,
                originalIndexFromUnfilteredResponse: response.recommendations.indexOf(r),
            });
        }

        return alternatives;
    }

    private extractFirstHaulageShippingAlternative(response: RecommendationResponse): AlternativeWithShippingOption | undefined {
        const alternatives = this.extractAlternatives(response);
        return alternatives.find(alternative => alternative.deliveryOptionTypes?.includes(SHIPPING.HAULAGE));
    }

    private async getHaulageShippingAlternativeForVariation(inputVariationId: string): Promise<AlternativeWithShippingOption | undefined> {
        const url = `/reco-core/recommendations/alternative/variations/${inputVariationId}`;
        try {
            const response = await fetch(url, {
                headers: { 'Content-Type': 'application/hal+json;profile="https://api.otto.de/profiles/recommendations+v1";charset=utf8' },
            });

            const data: RecommendationResponse = await response.json();
            return this.extractFirstHaulageShippingAlternative(data);
        } catch (error) {
            if (this.logger) {
                this.logger.logError({
                    message: `Error fetching better alternative for variation ${inputVariationId}: ${(error as Error).message}`,
                });
            }
        }
        return undefined;
    }

    private performViewTracking(alternativeIndex: number): void {
        this.trackView({
            product_ShippingAlternative: 'available',
            product_ShippingAlternativeIndex: `${alternativeIndex}`,
        });
    }

    private registerClickHandler(alternativeIndex: number): void {
        const betterShippingAlternativeAnchor = document.body.querySelector('.js_pdp_better-shipping-alternative__border-frame');

        betterShippingAlternativeAnchor?.addEventListener('click', () =>
            this.trackEvent({
                product_ShippingAlternative: 'clicked',
                product_ShippingAlternativeIndex: `${alternativeIndex}`,
            })
        );
        betterShippingAlternativeAnchor?.addEventListener('click', () =>
            this.trackOnNextPageImpression({
                product_BetterShippingAlternativeClicked: ['true'],
                product_ShippingAlternativeIndex: [`${alternativeIndex}`],
            })
        );
    }
}
